import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Megaphone, Globe, Clock, CheckCircle2, AlertCircle, Handshake, List } from "lucide-react";
import { useBiens } from "@/hooks/useBiens";
import { isForSale } from "@/lib/transaction";
import { DataTable } from "@/v2/components/DataTable";
import { usePublicationColumns } from "@/v2/components/publication/usePublicationColumns";
import { BienDetailModal } from "@/v2/components/BienDetailModal";
import { useLatestPortalPublicationsByBien } from "@/hooks/usePortalPublications";
import { cn } from "@/lib/utils";
import { hasUnpublishedChanges } from "@/lib/publicationHelpers";
import { isPublishable, type PublishGateInput } from "@/lib/publishGate";
import type { BienRow } from "@/schemas/bien";

/**
 * Workspace Publication SALES (V1 DRAFT — chantier Catalogue Sales 2026-07-12).
 *
 * Pendant vente du workspace Publication location (PublicationWorkspacePage) :
 * l'équipe Sales y suit la préparation et la mise en ligne de SES biens
 * (`transaction = "vente"` uniquement). Mêmes 5 onglets que la location
 * depuis le 2026-09-16 (À préparer / Prêts à publier / Publiés / Modifs non
 * poussées / Sous option), badges + fiche en modale. L'action
 * Publier/Dépublier elle-même vit dans la fiche bien
 * (BienDetailPage → useMarkBienAsPublished + statutPublication) — comme pour
 * la location, le workspace ne fait que router vers elle.
 */

type Tab = "all" | "todo" | "ready" | "published" | "stale" | "sousOption";

// Délègue au helper canonique (src/lib/publicationHelpers.ts) — même pattern
// que le workspace location, pour éviter la dérive entre buckets, bannière
// fiche bien et tuile dashboard.
const isStale = (b: BienRow) =>
  hasUnpublishedChanges({
    statutPublication: b.statutPublication,
    updated_at: b.updated_at,
    last_published_at: b.last_published_at,
    content_updated_at: b.content_updated_at,
  } as Parameters<typeof hasUnpublishedChanges>[0]);

export function PublicationVenteWorkspacePage() {
  const { t } = useTranslation();
  // PAS de roleOverride "publication_suivi" ici (contrairement au workspace
  // Publication location) : le rôle `vente` a ses propres field_permissions
  // (migration 097) qui couvrent statutPublication / prix / sousOption — un
  // override publication_suivi les lui STRIPPERAIT (prix ∉ perms publication).
  // c_level / admin voient aussi tout avec leur propre rôle.
  const { data: biens, isLoading } = useBiens();
  // Statut Immoweb réel par bien (colonne « Immoweb ») — demande Thibaud 2026-09-16.
  const { data: immowebByBien } = useLatestPortalPublicationsByBien("immoweb");
  const [tab, setTab] = useState<Tab>("todo");
  // Fiche ouverte en modale par-dessus la liste, comme Publication Location :
  // naviguer vers /v2/biens/:id faisait basculer la sidebar sur « Catalogue
  // Vente » et perdait la liste en cours (retour Thibaud 2026-09-16).
  const [modalBienId, setModalBienId] = useState<string | null>(null);

  const buckets = useMemo(() => {
    // Périmètre Sales : uniquement les biens en vente. Pas d'autre gate en V1
    // (les buckets ci-dessous suffisent à séparer préparation / publiable /
    // en ligne).
    const list = (biens || []).filter((b) => isForSale(b.transaction));
    // Onglet « Sous option » EXCLUSIF (demande Thibaud 2026-09-16, mêmes 5
    // onglets que Publication Location) : un bien en négociation ne peut pas
    // être publié tant que la case reste cochée.
    const sousOption = list.filter((b) => b.sousOption === true);
    const open = list.filter((b) => b.sousOption !== true);
    // Buckets alignés sur le garde-fou de publication (publishGate) : "Prêts
    // à publier" = exactement les biens dont le bouton Publier (fiche) est
    // actif. "À préparer" = le reste (non publié). « Modifs non poussées » =
    // publié mais modifié depuis le dernier envoi (isStale), comme en location.
    const todo = open.filter(
      (b) => b.statutPublication !== "Publié" && !isPublishable(b as unknown as PublishGateInput).ok,
    );
    const ready = open.filter(
      (b) => b.statutPublication !== "Publié" && isPublishable(b as unknown as PublishGateInput).ok,
    );
    const published = open.filter((b) => b.statutPublication === "Publié" && !isStale(b));
    const stale = open.filter(isStale);
    // « Tous » = tout le périmètre de la page, sans découpage (demande Thibaud 2026-09-16).
    return { all: list, todo, ready, published, stale, sousOption };
  }, [biens]);

  const tabs: { key: Tab; label: string; icon: typeof AlertCircle; count: number; color: string }[] = [
    {
      key: "all",
      label: t("v2.publicationWs.tabs.all"),
      icon: List,
      count: buckets.all.length,
      color: "text-text-muted",
    },
    {
      key: "todo",
      label: t("v2.publicationWs.tabs.todo"),
      icon: AlertCircle,
      count: buckets.todo.length,
      color: "text-warning",
    },
    {
      key: "ready",
      label: t("v2.publicationWs.tabs.ready"),
      icon: CheckCircle2,
      count: buckets.ready.length,
      color: "text-accent",
    },
    {
      key: "published",
      label: t("v2.publicationWs.tabs.published"),
      icon: Globe,
      count: buckets.published.length,
      color: "text-success",
    },
    {
      key: "stale",
      label: t("v2.publicationWs.tabs.stale"),
      icon: Clock,
      count: buckets.stale.length,
      color: "text-danger",
    },
    {
      key: "sousOption",
      label: t("v2.publicationWs.tabs.sousOption"),
      icon: Handshake,
      count: buckets.sousOption.length,
      color: "text-warning",
    },
  ];

  const visibleRows = buckets[tab] || [];

  // Colonnes COMMUNES Location / Vente (décision Thibaud 2026-09-16).
  const { columns, minWidth } = usePublicationColumns({ transaction: "vente", immowebByBien });

  return (
    <div className="p-4 md:p-6">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-2">
          <Megaphone size={22} className="text-accent" />
          {t("v2.nav.publicationVente")}
        </h1>
        <p className="text-sm text-text-muted mt-1">{t("vente.publication.subtitle")}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-border overflow-x-auto">
        {tabs.map((tabItem) => {
          const Icon = tabItem.icon;
          const isActiveTab = tab === tabItem.key;
          return (
            <button
              key={tabItem.key}
              type="button"
              onClick={() => setTab(tabItem.key)}
              className={cn(
                "px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 cursor-pointer whitespace-nowrap",
                isActiveTab
                  ? "border-accent text-text-primary"
                  : "border-transparent text-text-muted hover:text-text-primary",
              )}
            >
              <Icon size={14} className={isActiveTab ? "text-accent" : tabItem.color} />
              <span>{tabItem.label}</span>
              <span
                className={cn(
                  "text-xs px-1.5 rounded",
                  isActiveTab ? "bg-accent-light text-accent" : "bg-gray-100 text-text-muted",
                )}
              >
                {tabItem.count}
              </span>
            </button>
          );
        })}
      </div>

      <DataTable
        rows={visibleRows}
        columns={columns}
        minWidth={minWidth}
        rowKey={(b) => b.id}
        onRowClick={(b) => setModalBienId(b.id)}
        searchAccessor={(b) =>
          [b.referenceInterne, b.reference, b.id, b.adresse, b.ville, b.projet]
            .filter(Boolean)
            .join(" ")
        }
        searchPlaceholder={t("v2.publicationWs.searchPlaceholder")}
        isLoading={isLoading}
        emptyLabel={t("v2.publicationWs.empty")}
      />

      <BienDetailModal bienId={modalBienId} onClose={() => setModalBienId(null)} />
    </div>
  );
}
